import React from "react";
import {
  Select as MuiSelect,
  SelectProps,
  MenuItem,
  FormControl,
  InputLabel,
  SxProps,
  Theme,
} from "@mui/material";

interface CustomSelectProps extends Omit<SelectProps, "variant"> {
  variant?: "outlined";
  sx?: SxProps<Theme>;
  label?: string;
}

/**
 * Standardized Select component matching the SearchBar design
 * Use this component for all dropdown selects across the website
 */
const Select: React.FC<CustomSelectProps> = ({
  sx,
  label,
  children,
  MenuProps,
  ...props
}) => {
  const baseSx: SxProps<Theme> = {
    borderRadius: 999,
    backgroundColor: (theme) =>
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.06)"
        : "rgba(0,0,0,0.04)",
    transition: "box-shadow 0.2s ease, background-color 0.2s ease",
    minHeight: 50,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: (theme) =>
        theme.palette.mode === "dark"
          ? "rgba(255,255,255,0.12)"
          : "rgba(0,0,0,0.12)",
      borderWidth: "1px",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: (theme) =>
        theme.palette.mode === "dark"
          ? "rgba(255,255,255,0.12)"
          : "rgba(0,0,0,0.12)",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: (theme) =>
        theme.palette.mode === "dark"
          ? "rgba(255,255,255,0.12)"
          : "rgba(0,0,0,0.12)",
      boxShadow: "none",
    },
    "& .MuiSelect-select": {
      py: 1.25,
      px: 3,
    },
  };

  // Default MenuProps with glass-card styling (matching create story style)
  const defaultMenuProps = {
    PaperProps: {
      elevation: 0,
      sx: {
        borderRadius: 3,
        backgroundColor: "transparent",
        backdropFilter: "blur(12px)",
        border: (theme: Theme) =>
          `1px solid ${
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.08)"
              : "rgba(0,0,0,0.08)"
          }`,
        boxShadow: (theme: Theme) =>
          theme.palette.mode === "dark"
            ? "0 2px 8px rgba(0,0,0,0.1)"
            : "0 2px 8px rgba(0,0,0,0.05)",
        maxHeight: 400,
        "& .MuiMenuItem-root": {
          "&:hover": {
            backgroundColor: (theme: Theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.08)"
                : "rgba(0,0,0,0.04)",
          },
        },
      },
    },
  };

  const combinedSx = Array.isArray(sx)
    ? [baseSx, ...sx]
    : sx
    ? [baseSx, sx]
    : [baseSx];

  const labelId = label ? `${props.id || "select-label"}-label` : undefined;

  if (label) {
    return (
      <FormControl fullWidth={props.fullWidth} size={props.size}>
        <InputLabel
          id={labelId}
          sx={{
            fontSize: "0.90rem",
            px: 1,
            "&.Mui-focused": {
              color: "text.secondary",
            },
          }}
        >
          {label}
        </InputLabel>
        <MuiSelect
          labelId={labelId}
          label={label}
          variant="outlined"
          sx={combinedSx}
          MenuProps={MenuProps || defaultMenuProps}
          {...props}
        >
          {children}
        </MuiSelect>
      </FormControl>
    );
  }

  return (
    <MuiSelect
      variant="outlined"
      sx={combinedSx}
      MenuProps={MenuProps || defaultMenuProps}
      {...props}
    >
      {children}
    </MuiSelect>
  );
};

export { MenuItem };
export default Select;

